import {DataStatus, showNotification} from 'platform/components';
import {Show} from 'platform/foundation';

import {isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {
  useGetMyWebPlatformQuery,
  useMyWebAuthenticateMutation,
  useUpdateMyWebDescriptionFormatMutation,
} from '@dms/api/sales';
import {AdvertisingPlatformCodeEnum} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import {useBranchId} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {
  MyWebAuthenticationFormData,
  PlatformAuthenticationFormMyWeb,
} from './components/PlatformAuthenticationFormMyWeb';
import {PlatformPhotoSettings} from './components/PlatformPhotoSettings';

const paramsTenant = undefined;

export function PlatformSettingsMyWeb() {
  const branchId = useBranchId();
  const params = {branchId};
  const {data: dataGeneral, isLoading, isError} = useGetMyWebPlatformQuery({params});
  const isPublishingOnTenant = isNotNil(branchId) && !!dataGeneral?.publishOnTenant;
  const {data: dataTenant} = useGetMyWebPlatformQuery(paramsTenant, {skip: isNil(branchId)});
  const [saveAuthentication] = useMyWebAuthenticateMutation();
  const [updateDescriptionFormat] = useUpdateMyWebDescriptionFormatMutation();

  const activeData = isPublishingOnTenant ? dataTenant : dataGeneral;
  const resolvedParams = isPublishingOnTenant ? paramsTenant : params;

  const onSubmitCredentials = (values: MyWebAuthenticationFormData) => {
    const {descriptionFormat, ...credentials} = values;

    const authPromise = saveAuthentication({
      body: credentials,
      params: resolvedParams,
    }).unwrap();

    if (descriptionFormat) {
      const formatPromise = updateDescriptionFormat({
        body: {descriptionFormat},
        params: resolvedParams,
      }).unwrap();

      return Promise.all([authPromise, formatPromise])
        .then(() => showNotification.success())
        .catch(handleApiError);
    }

    return authPromise.then(() => showNotification.success()).catch(handleApiError);
  };

  return (
    <Show whenFeatureEnabled={featureFlags.ADVERTISEMENT_GENERAL_SETTINGS}>
      <DataStatus isLoading={isLoading} isError={isError}>
        <PlatformAuthenticationFormMyWeb
          defaultValues={activeData?.credentials}
          defaultDescriptionFormat={activeData?.descriptionFormat}
          isPublishingOnTenant={isPublishingOnTenant}
          onSubmit={onSubmitCredentials}
        />
        <PlatformPhotoSettings
          platformCode={AdvertisingPlatformCodeEnum.MYWEB}
          isPublishingOnTenant={isPublishingOnTenant}
        />
      </DataStatus>
    </Show>
  );
}
